import { Box, Typography } from '@mui/material';
import { EmailOutlined, AccountCircleOutlined, PersonSearchOutlined, TodayOutlined } from '@mui/icons-material';
import { clientScheduleSessionRoute, ROUTING_CONFIG } from '@uplift/constants';
import { ContactInfoRow, StackableCard, useMessagingContext } from '@uplift/ui-v2';
import { Link, useLocation } from 'react-router-dom';
import { ProviderAvatar } from './provider-card-avatar.component';
import { useStyles } from './provider-card.styles';
import { useL10n } from '@uplift/hooks';
import { ProviderCardViewProps } from './provider-card.component';
import { useProviderPublicMatchingProfile } from '@uplift/queries';
import { useCanScheduleWithProvider } from '../../../hooks/use-can-schedule-with-provider';
import { isRematchEligibleRole } from '../../rematching/rematch-eligibility';

export const ProviderCardDesktop: React.VFC<ProviderCardViewProps> = ({ matchId, providerType, userName, provider }) => {
  const location = useLocation();
  const l10n = useL10n();
  const { classes } = useStyles();
  const { data: publicProfile } = useProviderPublicMatchingProfile(provider.id, { enabled: !!provider?.id, suspense: true });
  const { canSchedule } = useCanScheduleWithProvider(provider.id, { suspense: true });
  const messagingContext = useMessagingContext();

  return (
    <StackableCard className={classes.content}>
      <Box className={classes.info}>
        <ProviderAvatar src={publicProfile?.avatarAsset?.url} size={56} />
        <Box className={classes.rightBlock}>
          <Box className={classes.nameLine}>
            <Typography variant="h6" className={classes.fullNameText}>
              {userName}
            </Typography>
            {publicProfile.profile.pronouns && (
              <Typography variant="body1">
                ({publicProfile.profile.pronouns})
              </Typography>
            )}
          </Box>
          <Typography color="textSecondary">
            {l10n.resources.provider.types[providerType]}
          </Typography>
        </Box>
      </Box>
      <Box className={classes.actions}>
        <ContactInfoRow
          icon={EmailOutlined}
          primary={(
            <Typography
              variant="body1"
              component="span"
              color="textPrimary"
              sx={{ textDecoration: 'underline', cursor: 'pointer' }}
              onClick={async () => {
                const conversationId = await messagingContext.createChat(provider.id);

                messagingContext.openModal(conversationId);
              }}
            >
              Open Chat
            </Typography>
          )}
          type="email"
        />

        {canSchedule && (
          <ContactInfoRow
            icon={TodayOutlined}
            primary={(
              <Box display="flex" gap=".5rem" pb="1px">
                <Typography variant="body1" component={Link} to={clientScheduleSessionRoute.createLink({ providerId: provider.id })} color="textPrimary">
                  Schedule Session
                </Typography>
              </Box>
            )}
          />
        )}

        <ContactInfoRow
          icon={AccountCircleOutlined}
          primary={(
            <Box display="flex" gap=".5rem" pb="1px">
              <Typography variant="body1" component={Link} to={`${ROUTING_CONFIG.careTeam}/${provider.id}`} color="textPrimary">
                {l10n.general.viewProfile}
              </Typography>
            </Box>
          )}
        />

        {isRematchEligibleRole(provider.medicalRole) && (
          <ContactInfoRow
            icon={PersonSearchOutlined}
            primary={(
              <Box display="flex" gap=".5rem" pb="1px">
                <Typography
                  variant="body1"
                  component={Link}
                  to={{ pathname: `/rematching/${matchId}`, state: { referrer: location.pathname } }}
                  color="textPrimary"
                >
                  {l10n.requestRematch.findANewProvider(l10n.resources.provider.roles[provider.medicalRole].toLowerCase())}
                </Typography>
              </Box>
            )}
          />
        )}
      </Box>
    </StackableCard>
  );
};
